import { Component, Input, OnDestroy } from "@angular/core";
import { Router, NavigationExtras } from "@angular/router";
import { AlertController, Platform } from "@ionic/angular";
import { Browser } from "@capacitor/browser";
import { MenuServiceProvider } from "src/app/service/index.providers";
import { LanguageService } from "src/app/service/language-service/language.service";

/**
 * Grilla reutilizable de anuncios ("Novedades"). Renderiza la misma tarjeta de
 * anuncio ({@link CardAnunciosComponent}) que la página de Anuncios y encapsula
 * las interacciones multimedia (zoom de imagen, reproducción de video/audio,
 * apertura de links y descarga de archivos) para que cualquier vista pueda
 * mostrar anuncios pasándole solo el listado y la URL base del multimedia.
 *
 * Pensada como componente "tonto": recibe la data ya cargada (ver
 * {@link AnnouncementsService}) y no hace requests por sí misma.
 */
@Component({
	selector: "app-announcements-grid",
	templateUrl: "./announcements-grid.component.html",
	styleUrls: ["./announcements-grid.component.scss"],
})
export class AnnouncementsGridComponent implements OnDestroy {
	/** Listado de anuncios a mostrar (forma cruda del endpoint de anuncios). */
	@Input() announcements: any[] = [];
	/** URL base del bucket para resolver imágenes/multimedia. */
	@Input() mediaBaseUrl = "";
	/** Imagen personalizada de moneda (rewards), si aplica. */
	@Input() coinCustomImage: string | null = null;

	zoom = false;
	/** Nombre del archivo de imagen en zoom (se concatena a {@link mediaBaseUrl}). */
	focusImageValue: string | null = null;
	/** Item completo seleccionado para el reproductor de video. */
	focusMediaValue: any = null;
	isVideoPlayerOpen = false;
	audioPlayerIsActive = false;
	cardSelectedIndex: number | null = null;
	language = "es";

	constructor(
		private router: Router,
		private platform: Platform,
		private alertCtrl: AlertController,
		private mp: MenuServiceProvider,
		private languageService: LanguageService,
	) {
		this.languageService.getCurrentLang().subscribe((lang) => (this.language = lang));
	}

	/** Tipo de media de un anuncio para elegir el render de la tarjeta. */
	mediaType(item: any): "video" | "audio" | "image" {
		const type = item?.media?.media_type;
		if (type === "video" || type === "vimeo" || type === "youtube") return "video";
		if (type === "audio") return "audio";
		return "image";
	}

	/** Click sobre el multimedia de la tarjeta: enruta según el tipo. */
	onMediaClick(item: any, index: number) {
		const type = item?.media?.media_type;
		if (type === "video" || type === "vimeo" || type === "youtube") {
			this.playVideo(item, index);
		} else if (type === "audio") {
			this.playAudio(item.media?.value, index, "grid-audio-container-" + index);
		} else {
			this.zoomImage(item, index);
		}
	}

	zoomImage(item?: any, index?: number) {
		this.zoom = !this.zoom;
		if (item) {
			this.focusImageValue = item.imagen;
			this.cardSelectedIndex = index ?? null;
		}
	}

	playVideo(item: any, index: number) {
		this.focusMediaValue = item;
		this.isVideoPlayerOpen = true;
		this.cardSelectedIndex = index;
	}

	closeVideoPlayer() {
		this.isVideoPlayerOpen = false;
	}

	playAudio(audioUrl: string, index: number, containerId: string) {
		this.audioPlayerIsActive = true;
		if (this.cardSelectedIndex !== index) {
			this.cardSelectedIndex = index;
			setTimeout(() => this.insertAudio(this.mediaBaseUrl + audioUrl, index, containerId), 100);
		}
	}

	private insertAudio(audioUrl: string, index: number, containerId: string) {
		const audioElement: any = document.createElement("audio");
		audioElement.id = "card-audio-player-" + index;
		audioElement.className = "mx-auto";
		audioElement.src = audioUrl;
		audioElement.autoplay = true;
		audioElement.controls = true;
		audioElement.controlsList = "nodownload";
		audioElement.addEventListener("play", (event: Event) => event.stopPropagation());
		audioElement.addEventListener("pause", (event: Event) => event.stopPropagation());
		const containerDiv = document.getElementById(containerId);
		if (containerDiv) containerDiv.appendChild(audioElement);
	}

	/** Detiene cualquier audio en reproducción y limpia el estado del player. */
	private mediaStopper() {
		const audio: any = document.getElementById(`card-audio-player-${this.cardSelectedIndex}`);
		if (audio) audio.pause();
		this.audioPlayerIsActive = false;
		this.cardSelectedIndex = null;
	}

	/** Botón "Ir": navega a un curso o a la sección interna indicada por el anuncio. */
	goToDestino(item: any) {
		if (item.course) {
			this.router.navigate([item.course]);
			return;
		}
		this.abrirVentana(item.destino);
	}

	private abrirVentana(destino: string) {
		const routeByDestino: Record<string, { path: string; extras?: NavigationExtras }> = {
			escuelas: { path: "cursos" },
			progreso: { path: "progreso" },
			cursos_extra: { path: "progreso", extras: { queryParams: { categoria: "extracurricular" } } },
			diplomas: { path: "certificados" },
			glosario: { path: "glosario" },
			ranking: { path: "ranking" },
			reuniones: { path: "lista-reuniones" },
			vademecum: { path: "vademecum" },
			preguntas_frec: { path: "faq" },
			ayuda: { path: "ayuda" },
		};
		const target = routeByDestino[destino] ?? { path: destino };
		this.mp.setSelectedPageRoute(target.path);
		this.router.navigate([target.path], target.extras ?? {});
	}

	async openLink(link: string) {
		if (this.platform.is("ios")) {
			await Browser.open({ url: link });
		} else if (this.platform.is("android")) {
			window.open(link, "_system", "location=yes");
		} else {
			window.open(link, "_blank");
		}
	}

	async confirmDownload(archivo: string) {
		const alert = await this.alertCtrl.create({
			header: this.language === "es" ? "Descargar" : "Download",
			subHeader: this.language === "es" ? "Te sugerimos utilizar una red WI-FI" : "",
			mode: "ios",
			buttons: [
				{ text: this.language === "es" ? "Cancelar" : "Cancel", role: "cancel" },
				{
					text: this.language === "es" ? "Continuar" : "Continue",
					handler: () => this.download(archivo),
				},
			],
		});
		await alert.present();
	}

	private async download(archivo: string) {
		const resp = this.mediaBaseUrl + archivo;
		if (this.platform.is("ios")) {
			await Browser.open({ url: resp });
		} else if (this.platform.is("android")) {
			window.open(resp, "_system", "location=yes");
		} else {
			window.open(resp, "_blank");
		}
	}

	ngOnDestroy() {
		this.mediaStopper();
		this.closeVideoPlayer();
	}
}
